import { useEffect, useState } from "react";
import { useTranslation } from "locales";
import providers from "lib/providers";
import useAuthUser from "./useAuthUser";

export interface ThreadItem {
  /** Message thread id — used to send (threadIds) and as the composer key. */
  id: string;
  name: string;
  subjectType: string;
  /** For 1:1 chats: the other participant's user id (route `/messages/:userId`). */
  userId?: string;
  /** For group chats: the group id (route `/messages/group/:groupId`). */
  groupId?: string;
  /**
   * The user's own Saved Messages entry. When the self-thread doesn't exist
   * yet the entry is synthetic (`id` is SAVED_MESSAGES_ID) — senders must
   * target `recipientIds: [userId]` instead of `threadIds` so the backend
   * creates the thread on first send.
   */
  isSelf?: boolean;
}

/** Sentinel id for the synthetic Saved Messages entry (no thread exists yet). */
export const SAVED_MESSAGES_ID = "__saved_messages__";

/**
 * Loads the current user's personal + group message threads for a chat picker
 * (Forward / Share-to-chat). Extracted from ForwardMessageModal so both it and
 * ShareTargetModal share the exact same loading/dedupe logic.
 *
 * `ThreadItem` additionally carries the navigation target (userId / groupId) so
 * a consumer can open the chat, not just send into it by thread id.
 */
export default function useThreadPicker() {
  // Renamed: `t` is taken by the thread iteration variable below.
  const { t: translate } = useTranslation();
  const { user } = useAuthUser();
  const [threads, setThreads] = useState<ThreadItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    const load = async () => {
      try {
        const [personal, groups] = await Promise.all([
          providers.MessageThreadsProvider.find({
            data: { userId: user.id, sort: "UPDATED_TIME,DESC", size: 30, hidden: false },
          }),
          providers.MessageThreadsProvider.find({
            data: { userId: user.id, subjectType: "group", sort: "UPDATED_TIME,DESC", size: 30, hidden: false },
          }),
        ]);

        const seen = new Set<string>();
        const items: ThreadItem[] = [];

        const addThreads = (result: any[], type: string) => {
          (result || []).forEach((t: any) => {
            if (seen.has(String(t.id))) return;
            seen.add(String(t.id));
            const subType = t.subject?.subjectType || type;
            let name: string;
            let userId: string | undefined;
            let groupId: string | undefined;
            let isSelf = false;
            if (subType === "group") {
              name = t._groupName || t.groupName || `Group ${t.subject?.subjectId || t.id}`;
              groupId = t.subject?.subjectId ? String(t.subject.subjectId) : undefined;
            } else {
              const otherUsers = (t.users || []).filter((u: any) => String(u.id) !== String(user.id));
              if (otherUsers.length === 0) {
                // Self-thread — label it like the sidebar does, not with the
                // user's own screen name.
                name = translate("Saved Messages");
                isSelf = true;
              } else {
                name = otherUsers.map((u: any) => u.screenName).join(", ") || `Chat ${t.id}`;
              }
              // Route by the opponent's id. For a self-thread (Saved Messages —
              // no other participant) fall back to the current user's id, which
              // resolves to that same thread, so the row stays navigable instead
              // of landing on /messages/<threadId> (parsed as a userId → fails).
              userId = otherUsers[0]?.id ? String(otherUsers[0].id) : String(user.id);
            }
            items.push({ id: String(t.id), name, subjectType: subType, userId, groupId, isSelf });
          });
        };

        addThreads(groups.result, "group");
        addThreads(personal.result, "personal");
        // No self-thread yet (the user never wrote to Saved Messages) —
        // offer a synthetic entry so Saved Messages is always a target.
        if (!items.some((i) => i.isSelf)) {
          items.unshift({
            id: SAVED_MESSAGES_ID,
            name: translate("Saved Messages"),
            subjectType: "personal",
            userId: String(user.id),
            isSelf: true,
          });
        }
        if (!cancelled) setThreads(items);
      } catch (e) {
        console.error("Failed to load threads:", e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  return { threads, loading };
}
